import { history } from '@umijs/max';
import React from 'react';
import { hasOAuth2Session } from '@/services/oauth2';
import { TENANT_CONTEXT_CHANGED_EVENT } from '@/adapters/tenantStorage';
import type {
  TechManagerDeletePreview,
  TechManagerResource,
} from '@/services/tech-manager/api';
import {
  completeTechManagerTask,
  convertTechManagerLead,
  createTechManagerFollowUp,
  createTechManagerOpportunity,
  createTechManagerResource,
  createTechManagerTask,
  deleteTechManagerResource,
  getTechManagerDeletePreview,
  getTechManagerDetail,
  getTechManagerList,
  getWorkbench,
  updateTechManagerResource,
  updateTechManagerResourceStatus,
} from '@/services/tech-manager/api';
import type {
  BusinessEntity,
  WorkbenchState,
  EntityKind,
  FollowUp,
  LeadConversionInput,
  Task,
} from './model';

export const collectionByKind: Record<EntityKind, keyof WorkbenchState> = {
  activity: 'activities',
  lead: 'leads',
  organization: 'organizations',
  contact: 'contacts',
  demand: 'demands',
  expert: 'experts',
  technology: 'technologies',
  supplier: 'suppliers',
  opportunity: 'opportunities',
};

const resourceByKind: Record<EntityKind, TechManagerResource> = {
  activity: 'activities',
  lead: 'leads',
  organization: 'organizations',
  contact: 'contacts',
  demand: 'demands',
  expert: 'experts',
  technology: 'technologies',
  supplier: 'organizations',
  opportunity: 'opportunities',
};

type EntityInput = Omit<BusinessEntity, 'id'>;
type EntityResult = BusinessEntity;

export interface WorkbenchStoreValue {
  state: WorkbenchState;
  loading: boolean;
  error: Error | null;
  retry: () => Promise<void>;
  createEntity: (
    type: EntityKind,
    payload: EntityInput,
  ) => Promise<EntityResult>;
  updateEntity: (
    type: EntityKind,
    id: string,
    patch: Partial<BusinessEntity>,
  ) => Promise<void>;
  updateEntityStatus: (
    type: EntityKind,
    id: string,
    status: string,
  ) => Promise<void>;
  previewDelete: (
    type: EntityKind,
    id: string,
  ) => Promise<TechManagerDeletePreview>;
  deleteEntity: (type: EntityKind, id: string) => Promise<void>;
  addFollowUp: (
    input: Omit<FollowUp, 'id' | 'createdAt' | 'owner'> & { owner?: string },
  ) => Promise<void>;
  addTask: (
    input: Omit<Task, 'id' | 'owner' | 'dueAt'> & {
      owner?: string;
      dueAt?: string;
    },
  ) => Promise<void>;
  completeTask: (id: string) => Promise<void>;
  convertLead: (
    input: LeadConversionInput,
  ) => Promise<Record<string, string | undefined>>;
  createOpportunityFromDemand: (demandId: string) => Promise<EntityResult>;
  exportWorkbenchData: () => void;
  exportDemoData: () => void;
}

export type DemoStoreValue = WorkbenchStoreValue;

const emptyWorkbenchState = (): WorkbenchState => ({
  version: 1,
  activities: [],
  leads: [],
  organizations: [],
  contacts: [],
  demands: [],
  experts: [],
  technologies: [],
  suppliers: [],
  opportunities: [],
  tasks: [],
  followUps: [],
  logs: [],
});

const emptyState = emptyWorkbenchState;

const asError = (value: unknown) =>
  value instanceof Error ? value : new Error(String(value));
const value = (raw: Record<string, unknown>, ...keys: string[]) =>
  keys.map((key) => raw[key]).find((item) => item != null);
const workflowKey = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const exportHiddenField = (key: string) =>
  key === 'id' ||
  key === 'version' ||
  key.endsWith('Id') ||
  key.endsWith('Uuid') ||
  ['isDeleted', 'dataSource', 'externalSystem', 'externalId'].includes(key);

function mapEntity(raw: Record<string, unknown>): BusinessEntity {
  return {
    ...(raw as unknown as BusinessEntity),
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: value(raw, 'description', 'remark') as string | undefined,
    owner: value(raw, 'owner', 'ownerName') as string | undefined,
    organizationName: value(raw, 'organizationName', 'organizerName') as
      | string
      | undefined,
    date: value(raw, 'date', 'startAt', 'startDate', 'estimatedStartDate') as
      | string
      | undefined,
    endDate: value(raw, 'endDate', 'endAt') as string | undefined,
    sourceId: value(raw, 'sourceId', 'sourceActivityId') as string | undefined,
    maturity: value(raw, 'maturity', 'maturityLevel') as string | undefined,
    risk: value(raw, 'risk', 'riskLevel') as string | undefined,
    value: value(raw, 'value', 'estimatedValue') as number | undefined,
  };
}

function mapTask(raw: Record<string, unknown>): Task {
  return {
    ...(raw as unknown as Task),
    id: String(raw.id),
    name: String(raw.name ?? raw.title ?? ''),
    entityType: raw.entityType as EntityKind,
    entityId: String(raw.entityId),
    entityName: String(raw.entityName ?? ''),
    owner: String(raw.owner ?? raw.ownerName ?? ''),
    dueAt: String(raw.dueAt ?? raw.dueDate ?? ''),
    priority: (raw.priority ?? '中') as Task['priority'],
    status: (raw.status ?? '待处理') as Task['status'],
  };
}

function mapLog(raw: Record<string, unknown>): WorkbenchState['logs'][number] {
  return {
    ...(raw as unknown as WorkbenchState['logs'][number]),
    id: String(raw.id),
    entityType: raw.entityType as EntityKind,
    entityId: String(raw.entityId),
    title: String(raw.title ?? raw.type ?? ''),
    detail: String(raw.detail ?? raw.content ?? ''),
    owner: String(raw.owner ?? raw.operatorName ?? ''),
    createdAt: String(raw.createdAt ?? raw.occurredAt ?? ''),
  };
}

const WorkbenchStoreContext = React.createContext<WorkbenchStoreValue | null>(null);

const usePathname = () => {
  const [pathname, setPathname] = React.useState(history.location.pathname);

  React.useEffect(
    () => history.listen(({ location }) => setPathname(location.pathname)),
    [],
  );

  return pathname;
};

export const WorkbenchStoreProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const pathname = usePathname();
  const [state, setState] = React.useState<WorkbenchState>(emptyWorkbenchState);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const loadedRef = React.useRef(false);

  React.useEffect(() => {
    const clearTenantState = () => {
      loadedRef.current = false;
      setState(emptyWorkbenchState());
      setError(null);
      setLoading(true);
    };

    window.addEventListener(TENANT_CONTEXT_CHANGED_EVENT, clearTenantState);
    return () => {
      window.removeEventListener(
        TENANT_CONTEXT_CHANGED_EVENT,
        clearTenantState,
      );
    };
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resources: EntityKind[] = [
        'activity',
        'organization',
        'contact',
        'lead',
        'demand',
        'expert',
        'technology',
        'opportunity',
      ];
      const [lists, workbench] = await Promise.all([
        Promise.all(
          resources.map((kind) =>
            getTechManagerList(resourceByKind[kind], { page: 1, pageSize: 50 }),
          ),
        ),
        getWorkbench(),
      ]);
      const next = emptyState();
      resources.forEach((kind, index) => {
        next[collectionByKind[kind]] = lists[index].items.map((item) =>
          mapEntity(item as Record<string, unknown>),
        ) as never;
      });
      const organizationNames = new Map(
        next.organizations.map((item) => [item.id, item.name]),
      );
      resources
        .filter((kind) => kind !== 'organization')
        .forEach((kind) => {
          const collection = collectionByKind[kind];
          next[collection] = (next[collection] as BusinessEntity[]).map(
            (item) => ({
              ...item,
              organizationName:
                item.organizationName ||
                (item.organizationId
                  ? organizationNames.get(item.organizationId)
                  : undefined),
            }),
          ) as never;
        });
      next.suppliers = next.organizations.filter((item) =>
        Boolean(item.isTechnologySupplier || (item as unknown as { raw?: { isTechnologySupplier?: boolean } }).raw?.isTechnologySupplier),
      );
      const dashboard = workbench as Record<string, unknown>;
      next.tasks = (
        Array.isArray(dashboard.pendingTasks) ? dashboard.pendingTasks : []
      ).map((item) => mapTask(item as Record<string, unknown>));
      next.logs = (
        Array.isArray(dashboard.recentLogs) ? dashboard.recentLogs : []
      ).map((item) => mapLog(item as Record<string, unknown>));
      setState(next);
    } catch (cause) {
      setState(emptyWorkbenchState());
      const failure = asError(cause);
      setError(failure);
      throw failure;
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (
      pathname === '/callback' ||
      pathname === '/user/login' ||
      !hasOAuth2Session()
    ) {
      setLoading(false);
      return;
    }
    if (loadedRef.current) {
      return;
    }
    loadedRef.current = true;
    void load().catch(() => {
      loadedRef.current = false;
    });
  }, [load, pathname]);

  const write = React.useCallback(
    async <T,>(operation: () => Promise<T>, onSuccess: (result: T) => void) => {
      try {
        const result = await operation();
        onSuccess(result);
        setError(null);
        return result;
      } catch (cause) {
        const failure = asError(cause);
        setError(failure);
        throw failure;
      }
    },
    [],
  );

  const valueForContext = React.useMemo<WorkbenchStoreValue>(() => {
    const createEntity: WorkbenchStoreValue['createEntity'] = async (
      type,
      payload,
    ) =>
      write(
        () =>
          createTechManagerResource(
            (type === 'supplier' ? 'organizations' : collectionByKind[type]) as never,
            (type === 'supplier'
              ? { ...payload, isTechnologySupplier: true }
              : payload) as unknown as Record<string, unknown>,
          ),
        (result) => {
          setState((previous) => {
            const entity = mapEntity(
              result as unknown as Record<string, unknown>,
            );
            const organizationName =
              entity.organizationName ||
              (entity.organizationId
                ? previous.organizations.find(
                    (item) => item.id === entity.organizationId,
                  )?.name
                : undefined);
            return {
              ...previous,
              [collectionByKind[type]]: [
                { ...entity, organizationName },
                ...(previous[collectionByKind[type]] as BusinessEntity[]),
              ],
            };
          });
        },
      );
    const updateEntity: WorkbenchStoreValue['updateEntity'] = async (
      type,
      id,
      patch,
    ) => {
      await write(
        () =>
          updateTechManagerResource(
            (type === 'supplier' ? 'organizations' : collectionByKind[type]) as never,
            id,
            patch as unknown as Record<string, unknown>,
          ),
        (result) => {
          const entity = mapEntity(
            result as unknown as Record<string, unknown>,
          );
          setState((previous) => ({
            ...previous,
            [collectionByKind[type]]: (
              previous[collectionByKind[type]] as BusinessEntity[]
            ).map((item) => (item.id === id ? { ...item, ...entity } : item)),
          }));
        },
      );
    };
    const previewDelete: WorkbenchStoreValue['previewDelete'] = (type, id) =>
      getTechManagerDeletePreview(
        type === 'supplier' ? 'organizations' : resourceByKind[type],
        id,
      );
    const deleteEntity: WorkbenchStoreValue['deleteEntity'] = async (type, id) => {
      await write(
        () =>
          deleteTechManagerResource(
            type === 'supplier' ? 'organizations' : resourceByKind[type],
            id,
          ),
        () => {
          setState((previous) => ({
            ...previous,
            [collectionByKind[type]]: (
              previous[collectionByKind[type]] as BusinessEntity[]
            ).filter((item) => item.id !== id),
            tasks: previous.tasks.filter(
              (item) => !(item.entityType === type && item.entityId === id),
            ),
            followUps: previous.followUps.filter(
              (item) => !(item.entityType === type && item.entityId === id),
            ),
            logs: previous.logs.filter(
              (item) => !(item.entityType === type && item.entityId === id),
            ),
          }));
        },
      );
      await load();
    };
    return {
      state,
      loading,
      error,
      retry: load,
      createEntity,
      updateEntity,
      previewDelete,
      deleteEntity,
      updateEntityStatus: async (type, id, status) => {
        await write(
          () =>
            updateTechManagerResourceStatus(
              (type === 'supplier' ? 'organizations' : collectionByKind[type]) as never,
              id,
              status,
            ),
          (result) => {
            const entity = mapEntity(
              result as unknown as Record<string, unknown>,
            );
            setState((previous) => ({
              ...previous,
              [collectionByKind[type]]: (
                previous[collectionByKind[type]] as BusinessEntity[]
              ).map((item) =>
                item.id === id
                  ? {
                      ...item,
                      ...entity,
                      organizationName:
                        entity.organizationName ?? item.organizationName,
                      status,
                      ...(type === 'opportunity'
                        ? { stage: status }
                        : {}),
                    }
                  : item,
              ),
            }));
          },
        );
      },
      addFollowUp: async (input) => {
        await write(
          () =>
            createTechManagerFollowUp(
              input.entityType,
              input.entityId,
              input as unknown as Record<string, unknown>,
            ),
          (result) => {
            const follow = result as Record<string, unknown>;
            const createdAt = String(
              follow.createdAt ?? follow.occurredAt ?? new Date().toISOString(),
            );
            const owner = String(
              follow.owner ?? follow.ownerName ?? input.owner ?? '',
            );
            const id = String(follow.id);
            setState((previous) => ({
              ...previous,
              followUps: [
                { ...input, owner, id, createdAt },
                ...previous.followUps,
              ],
              logs: [
                {
                  id,
                  entityType: input.entityType,
                  entityId: input.entityId,
                  title: input.type,
                  detail: input.content,
                  owner,
                  createdAt,
                },
                ...previous.logs,
              ],
            }));
          },
        );
      },
      addTask: async (input) => {
        await write(
          () =>
            createTechManagerTask(
              input.entityType,
              input.entityId,
              input as unknown as Record<string, unknown>,
            ),
          (result) =>
            setState((previous) => ({
              ...previous,
              tasks: [
                mapTask(result as Record<string, unknown>),
                ...previous.tasks,
              ],
            })),
        );
      },
      completeTask: async (id) => {
        await write(
          () => completeTechManagerTask(id),
          (result) =>
            setState((previous) => ({
              ...previous,
              tasks: previous.tasks.map((item) =>
                item.id === id
                  ? mapTask(result as Record<string, unknown>)
                  : item,
              ),
            })),
        );
      },
      convertLead: async (input) => {
        const result = (await write(
          () =>
            convertTechManagerLead(input.leadId, {
              ...input,
              idempotencyKey: workflowKey(`lead-${input.leadId}`),
            }),
          () => {
            setState((previous) => ({
              ...previous,
              leads: previous.leads.map((item) =>
                item.id === input.leadId ? { ...item, status: '已转化' } : item,
              ),
            }));
          },
        )) as Record<string, string | null | undefined>;
        const [organization, contact, demand, opportunity] = await Promise.all([
          result.organizationId
            ? getTechManagerDetail('organizations', result.organizationId)
            : undefined,
          result.contactId
            ? getTechManagerDetail('contacts', result.contactId)
            : undefined,
          result.demandId
            ? getTechManagerDetail('demands', result.demandId)
            : undefined,
          result.opportunityId
            ? getTechManagerDetail('opportunities', result.opportunityId)
            : undefined,
        ]);
        setState((previous) => {
          const prepend = (items: BusinessEntity[], entity: unknown) => {
            if (!entity) return items;
            const next = mapEntity(entity as Record<string, unknown>);
            return [next, ...items.filter((item) => item.id !== next.id)];
          };
          return {
            ...previous,
            organizations: prepend(previous.organizations, organization),
            contacts: prepend(previous.contacts, contact),
            demands: prepend(previous.demands, demand),
            opportunities: prepend(previous.opportunities, opportunity),
          };
        });
        return Object.fromEntries(
          Object.entries(result).map(([key, item]) => [key, item ?? undefined]),
        ) as Record<string, string | undefined>;
      },
      createOpportunityFromDemand: async (demandId) => {
        const demand = state.demands.find((item) => item.id === demandId);
        if (!demand) throw new Error('技术需求不存在');
        return write(
          () =>
            createTechManagerOpportunity(demandId, {
              name: `${demand.name}合作商机`,
              type: demand.type || '技术合作',
              priority: demand.priority || '中',
              ownerName: demand.owner || undefined,
              solutionSummary:
                demand.description || `围绕「${demand.name}」推进技术合作`,
              idempotencyKey: workflowKey(`opp-${demandId}`),
            }),
          (result) => {
            const entity = mapEntity(
              result as unknown as Record<string, unknown>,
            );
            setState((previous) => ({
              ...previous,
              opportunities: [entity, ...previous.opportunities],
            }));
          },
        );
      },
      exportWorkbenchData: () => {
        const blob = new Blob(
          [
            JSON.stringify(
              state,
              (key, item) => (exportHiddenField(key) ? undefined : item),
              2,
            ),
          ],
          { type: 'application/json' },
        );
        const anchor = document.createElement('a');
        anchor.href = URL.createObjectURL(blob);
        anchor.download = '技术经理工作台业务数据.json';
        anchor.click();
        URL.revokeObjectURL(anchor.href);
      },
      exportDemoData: () => {
        const blob = new Blob(
          [
            JSON.stringify(
              state,
              (key, item) => (exportHiddenField(key) ? undefined : item),
              2,
            ),
          ],
          { type: 'application/json' },
        );
        const anchor = document.createElement('a');
        anchor.href = URL.createObjectURL(blob);
        anchor.download = '技术经理工作台业务数据.json';
        anchor.click();
        URL.revokeObjectURL(anchor.href);
      },
    };
  }, [state, loading, error, load, write]);

  return (
    <WorkbenchStoreContext.Provider value={valueForContext}>
      {children}
    </WorkbenchStoreContext.Provider>
  );
};

export function useWorkbenchStore() {
  const context = React.useContext(WorkbenchStoreContext);
  if (!context) throw new Error('useWorkbenchStore 必须在 WorkbenchStoreProvider 内使用');
  return context;
}

export const DemoDataProvider = WorkbenchStoreProvider;
export const useDemoData = useWorkbenchStore;
